import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Box,
  Text,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { FiSearch, FiUsers, FiBriefcase } from "react-icons/fi";
import { logEvent } from "helpers/analytics";
import { useHistory } from "react-router";

interface HireOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HireOptionsModal: React.FC<HireOptionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useHistory().push;

  const handleSelectRecruit = () => {
    logEvent("service_option_click", { type: "recruit_tool" });
    navigate("/businesses#laborhack-recruit");
    onClose();
  };

  const handleSelectOutsource = () => {
    logEvent("service_option_click", { type: "consulting" });
    navigate("/businesses#recruitment-outsourcing");
    onClose();
  };

  const handleSelectProject = () => {
    logEvent("service_option_click", { type: "qualification_tools" });
    navigate("/businesses#project-outsourcing");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent maxW="850px" p={{ base: 2, md: 4 }} borderRadius="0">
        <ModalHeader p={{ base: 2, md: 4 }} pb={{ base: 2, md: 2 }}>
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="medium"
            color="gray.900"
          >
            How do you want to hire talent?
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={{ base: 2, md: 4 }}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Box
              as="button"
              onClick={handleSelectRecruit}
              textAlign="left"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="0"
              p={4}
              bg="white"
              transition="all 0.2s ease"
              _hover={{
                shadow: "md",
                transform: "translateY(-2px)",
                borderColor: "flatteredFlamingo.500",
              }}
              _active={{ transform: "translateY(0px)" }}
            >
              <Box mb={3} display="inline-block">
                <Box
                  rounded="full"
                  p={2.5}
                  bg="flatteredFlamingo.50"
                  borderWidth="1px"
                  borderColor="flatteredFlamingo.200"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={FiSearch}
                    color="flatteredFlamingo.600"
                    boxSize={5}
                  />
                </Box>
              </Box>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontFamily={"AeonikPro"}
                fontWeight="semibold"
                color="gray.900"
              >
                Use our recruitment platform
              </Text>
              <Text fontSize={{ base: "sm", md: "sm" }} color="gray.600" mt={1}>
                Use LaborHack Recruit to source, screen and hire. Get pre-vetted
                workers for every job.
              </Text>
            </Box>

            <Box
              as="button"
              onClick={handleSelectOutsource}
              textAlign="left"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="0"
              p={4}
              bg="white"
              transition="all 0.2s ease"
              _hover={{
                shadow: "md",
                transform: "translateY(-2px)",
                borderColor: "flatteredFlamingo.500",
              }}
              _active={{ transform: "translateY(0px)" }}
            >
              <Box mb={3} display="inline-block">
                <Box
                  rounded="full"
                  p={2.5}
                  bg="flatteredFlamingo.50"
                  borderWidth="1px"
                  borderColor="flatteredFlamingo.200"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={FiUsers}
                    color="flatteredFlamingo.600"
                    boxSize={5}
                  />
                </Box>
              </Box>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontFamily={"AeonikPro"}
                fontWeight="semibold"
                color="gray.900"
              >
                Outsource hiring
              </Text>
              <Text fontSize={{ base: "sm", md: "sm" }} color="gray.600" mt={1}>
                Let us manage your hiring process, from role scoping to
                shortlisting and interview coordination.
              </Text>
            </Box>

            <Box
              as="button"
              onClick={handleSelectProject}
              textAlign="left"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="0"
              p={4}
              bg="white"
              transition="all 0.2s ease"
              _hover={{
                shadow: "md",
                transform: "translateY(-2px)",
                borderColor: "flatteredFlamingo.500",
              }}
              _active={{ transform: "translateY(0px)" }}
            >
              <Box mb={3} display="inline-block">
                <Box
                  rounded="full"
                  p={2.5}
                  bg="flatteredFlamingo.50"
                  borderWidth="1px"
                  borderColor="flatteredFlamingo.200"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={FiBriefcase}
                    color="flatteredFlamingo.600"
                    boxSize={5}
                  />
                </Box>
              </Box>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontFamily={"AeonikPro"}
                fontWeight="semibold"
                color="gray.900"
              >
                Build project crew
              </Text>
              <Text fontSize={{ base: "sm", md: "sm" }} color="gray.600" mt={1}>
                {`Secure the right workers for projects of any scale, from start to finish.`}
              </Text>
            </Box>
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HireOptionsModal;
